import {
  DefaultApi,
  EventFormat,
  GetUpdatesRequest,
  JsGetActiveContractsResponse,
  JsGetUpdatesResponse,
  Event,
  CreatedEvent,
  JsContractEntryOneOf,
  Filters,
  GetActiveContractsRequest,
  ApiException,
  JsCantonError,
} from '@digitalasset/json-api-ts-client';
import { WSSupport } from './WSSupport';

interface ACSUpdateConfig {
  maxEventsBeforePrune: number;
  safeOffsetDeltaForPrune: number;
  maxUpdatesToFetch: number;
  wsTimeoutBeforeFirstElement: number;
}

export const ACS_UPDATE_CONFIG: ACSUpdateConfig = {
  // How many events do we accumulate before we prune (compact) the ACS history - set to 0 to enable to compact all events, which is more efficient as long as application always ask for increasing (or equal) offsets
  maxEventsBeforePrune: 150,
  // When we compact the ACS history, we keep all events within this offset delta of the last seen update offset - set 0 to allow to compact everything
  safeOffsetDeltaForPrune: 200,
  // How many updates do we fetch at once when fetching updates - if there are more updates, we will fetch again until we have caught up (returned data is always complete to the requested endInclusive offset - even if that means multiple fetches)
  maxUpdatesToFetch: 100,
  // Timeout for receiving the first element from the WebSocket stream when reading the ACS - if this timeout is reached before the first element is returned, we fall back to HTTP
  // lower values will make the UI more responsive in case of problems, but may lead to unnecessary fallbacks
  wsTimeoutBeforeFirstElement: 600000,
};

// Config attached to window for easier debugging / live configuration via browser console
/* eslint-disable */
if (typeof window !== 'undefined') {
  (window as any)['ACS_UPDATE_CONFIG'] = ACS_UPDATE_CONFIG;
}
/* eslint-enable */

export interface ACSKey {
  party?: string; // undefined or party
  templateId?: string; // either templateId or interfaceId must be set
  interfaceId?: string;
}

export class ACSContainer {
  private acsSet: ACSSet | null = null;

  constructor(orginalContainer: ACSContainer | null = null) {
    this.acsSet = orginalContainer ? orginalContainer.acsSet : null;
  }

  async update(
    offset: number,
    key: ACSKey,
    api: DefaultApi,
    wsSupport: WSSupport,
  ): Promise<Array<JsGetActiveContractsResponse>> {
    if (this.acsSet === null || !wsSupport.enabled()) {
      const acs = await ACSContainer.readACS(offset, key, api, wsSupport);
      this.acsSet = acs;
      return ACSContainer.calculateAt(acs, offset);
    }
    if (this.acsSet.lastUpdateOffset > offset) {
      return ACSContainer.calculateAt(this.acsSet, offset);
    }
    if (this.acsSet.acsOffset > offset) {
      this.acsSet = null; // reset and reinitialize
      return this.update(offset, key, api, wsSupport);
    }
    if (this.acsSet.updates.length >= ACS_UPDATE_CONFIG.maxEventsBeforePrune) {
      this.acsSet = await ACSContainer.compact(this.acsSet);
    }
    const updates = await ACSContainer.updateContracts(
      this.acsSet.lastUpdateOffset,
      offset,
      ACSContainer.createEventFormat(key),
      api,
    );

    const [newEvents, newOffset] = ACSContainer.extractEvents(
      this.acsSet.lastUpdateOffset,
      updates,
    );

    if (newOffset > this.acsSet.lastUpdateOffset) {
      this.acsSet = {
        ...this.acsSet,
        lastUpdateOffset: newOffset,
        updates: this.acsSet.updates.concat(newEvents),
      };
    }
    // in practise length should never be > maxUpdatesToFetch only equal (server should never return more than limit in query). This is just a safeguard.
    if (updates.length >= ACS_UPDATE_CONFIG.maxUpdatesToFetch) {
      // recall this to get more updates
      return this.update(offset, key, api, wsSupport);
    }
    this.acsSet = {
      ...this.acsSet,
      lastUpdateOffset: offset, // we have caught up
    };

    return ACSContainer.calculateAt(this.acsSet, offset);
  }

  private static readACSUsingWs(
    offset: number,
    key: ACSKey,
    wsSupport: WSSupport,
  ): Promise<ACSSet> {
    const wsACSURL = `${wsSupport.baseUrl}/v2/state/active-contracts`;
    const format = ACSContainer.createEventFormat(key);
    const request: GetActiveContractsRequest = {
      activeAtOffset: offset,
      verbose: false,
      eventFormat: format,
    };
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsACSURL, wsSupport.extractProtocols());
      const results: JsGetActiveContractsResponse[] = [];
      let finished = false;
      let error: Error | null = null;
      setTimeout(() => {
        if (!finished && !error && results.length === 0) {
          error = Error(
            `No data received from WebSocket ${wsACSURL} within ${ACS_UPDATE_CONFIG.wsTimeoutBeforeFirstElement}ms`,
          );
          wsSupport.reportError(error);
          reject(error);
          ws.close();
        }
      }, ACS_UPDATE_CONFIG.wsTimeoutBeforeFirstElement);
      ws.onopen = () => {
        ws.send(JSON.stringify(request));
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const data = JSON.parse(event.data) as JsGetActiveContractsResponse;
          results.push(data);
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      };

      ws.onerror = () => {
        error = new Error('WebSocket error');
        wsSupport.reportError(error);
        reject(error);
      };

      ws.onclose = () => {
        finished = true;
        if (!error) {
          wsSupport.reportSuccess();
          resolve({
            acsOffset: offset,
            initialAcs: results,
            lastUpdateOffset: offset,
            updates: [],
          });
        }
      };
    });
  }

  private static extractEvents(
    fromOffset: number,
    updates: Array<JsGetUpdatesResponse>,
  ): [Array<ACUpdateEvent>, number] {
    const newEvents: Array<ACUpdateEvent> = [];
    let newOffset = fromOffset;
    updates.forEach((update) => {
      if ('Transaction' in update.update) {
        const transaction = update.update.Transaction;
        const trOffset = transaction.value.offset;
        if (trOffset > newOffset) {
          const events: Array<Event> = transaction.value.events ?? [];
          events.forEach((event) => {
            if ('CreatedEvent' in event) {
              const acUpdate: ACUpdateEvent = {
                created: event.CreatedEvent,
                archivedContractId: null,
                offset: trOffset,
                workflowId: transaction.value.workflowId,
                synchronizerId: transaction.value.synchronizerId,
              };
              newEvents.push(acUpdate);
              newOffset = trOffset;
            } else if ('ArchivedEvent' in event) {
              const archivedEvent = event.ArchivedEvent;
              const acUpdate: ACUpdateEvent = {
                created: null,
                archivedContractId: archivedEvent.contractId,
                offset: trOffset,
                workflowId: transaction.value.workflowId,
                synchronizerId: transaction.value.synchronizerId,
              };
              newEvents.push(acUpdate);
              newOffset = trOffset;
            }
          });
        }
      } else if ('OffsetCheckpoint' in update.update) {
        const checkpoint = update.update.OffsetCheckpoint;
        newOffset = checkpoint.value.offset;
      } else {
        console.log(`ACS Update got unknown update type: ${JSON.stringify(update.update)}`);
      }
    });
    return [newEvents, newOffset];
  }

  private static async updateContracts(
    startOffset: number,
    endOffset: number,
    eventFormat: EventFormat,
    api: DefaultApi,
  ): Promise<Array<JsGetUpdatesResponse>> {
    const updates: GetUpdatesRequest = {
      beginExclusive: startOffset,
      endInclusive: endOffset,
      updateFormat: {
        includeTransactions: {
          eventFormat,
          transactionShape: 'TRANSACTION_SHAPE_ACS_DELTA',
        },
      },
      verbose: false,
    };
    return api.postV2UpdatesFlats(updates, ACS_UPDATE_CONFIG.maxUpdatesToFetch, 1000);
  }

  // we assume all the data is already loaded
  private static calculateAt(
    acs: ACSSet,
    offset: number,
  ): Promise<Array<JsGetActiveContractsResponse>> {
    if (acs.acsOffset > offset) {
      return Promise.reject(
        Error(
          // This should never happen as we check this before calling calculateAt
          `Cannot calculate ACS at offset ${offset} when initial ACS is at ${acs.acsOffset}`,
        ),
      );
    }
    const addedContracts: Array<JsGetActiveContractsResponse> = [];
    const removedContractIds: Set<string> = new Set();

    acs.updates.forEach((update) => {
      if (update.offset <= offset) {
        if (update.created) {
          const createdEv: CreatedEvent = update.created;

          const activeContract: JsGetActiveContractsResponse = {
            workflowId: update.workflowId ?? '',
            contractEntry: {
              JsActiveContract: {
                createdEvent: createdEv,
                synchronizerId: update.synchronizerId ?? '',
                reassignmentCounter: 0,
              },
            },
          };
          addedContracts.push(activeContract);
        } else if (update.archivedContractId) {
          removedContractIds.add(update.archivedContractId);
        }
      }
    });
    const createdContracts = acs.initialAcs.concat(addedContracts);
    const result = createdContracts.filter(
      (contract) =>
        !removedContractIds.has(
          (contract.contractEntry as JsContractEntryOneOf).JsActiveContract.createdEvent.contractId,
        ),
    );
    return Promise.resolve(result);
  }

  private static async compact(acs: ACSSet): Promise<ACSSet> {
    const newAcsOffset = Math.max(
      acs.acsOffset,
      acs.lastUpdateOffset - ACS_UPDATE_CONFIG.safeOffsetDeltaForPrune,
    );
    if (newAcsOffset > acs.acsOffset) {
      const responses = await ACSContainer.calculateAt(acs, newAcsOffset);
      return {
        acsOffset: newAcsOffset,
        initialAcs: responses,
        lastUpdateOffset: acs.lastUpdateOffset,
        updates: acs.updates.filter((update) => update.offset > newAcsOffset),
      };
    }
    return acs;
  }

  private static async readACS(
    offset: number,
    key: ACSKey,
    api: DefaultApi,
    wsSupport: WSSupport,
  ): Promise<ACSSet> {
    if (wsSupport.enabled()) {
      return ACSContainer.readACSUsingWs(offset, key, wsSupport).catch(() => {
        console.log('Falling back to HTTP for ACS read');
        return ACSContainer.readHttpACS(offset, key, api, wsSupport);
      });
    }
    return ACSContainer.readHttpACS(offset, key, api, wsSupport);
  }

  private static async readHttpACS(
    offset: number,
    key: ACSKey,
    api: DefaultApi,
    wsSupport: WSSupport,
  ): Promise<ACSSet> {
    const format = ACSContainer.createEventFormat(key);
    // 0 means no limit - we handle the case where the result is too large below
    const effectiveQueryLimit = wsSupport.queryLimit === 0 ? undefined : wsSupport.queryLimit;
    const acs = await api
      .postV2StateActiveContracts(
        {
          activeAtOffset: offset,
          verbose: false,
          eventFormat: format,
        },
        effectiveQueryLimit,
      )
      .catch((error: ApiException<JsCantonError>) => {
        // this catch clause is specifically to handle the case where the ACS is too large to be returned in one go, but client has not set a query limit (or it is 0)
        // this allows to extract the server limit from the error message and retry with that limit
        // whole section can be removed once server side pagination is implemented (or websockets work without problems)
        if (error.code !== 413 || effectiveQueryLimit !== undefined) {
          throw error;
        }
        const errorMessage = error?.body?.cause ?? '';
        const matchNmb = errorMessage.match(/limit\s*\((\d+)\)/);

        if (!matchNmb || matchNmb.length <= 1) {
          console.log(`Could not extract limit from error message: ${errorMessage}`);
          throw error;
        }

        const serverLimit = parseInt(matchNmb[1], 10);
        if (Number.isNaN(serverLimit)) {
          console.log(`Could not extract server limit from error message: ${errorMessage}`);
          throw error;
        }
        console.error(
          `The result size limit of server ${serverLimit} was exceeded. In order to keep application running limit is added to active-contracts query - this means that potentially not all the data is displayed.`,
        );
        // in order to avoid repeated extraction of the limit from the error message, we set it in wsSupport - this means that subsequent queries will also use that limit
        wsSupport.setQueryLimit(serverLimit);
        return api.postV2StateActiveContracts(
          {
            activeAtOffset: offset,
            verbose: false,
            eventFormat: format,
          },
          serverLimit,
        );
      });
    return {
      acsOffset: offset,
      initialAcs: acs,
      lastUpdateOffset: offset,
      updates: [],
    };
  }

  private static createTemplateFilter(templateId: string): Filters {
    return {
      cumulative: [
        {
          identifierFilter: {
            TemplateFilter: {
              value: {
                templateId,
                includeCreatedEventBlob: false,
              },
            },
          },
        },
      ],
    };
  }

  private static createInterfaceFilter(interfaceId: string): Filters {
    return {
      cumulative: [
        {
          identifierFilter: {
            InterfaceFilter: {
              value: {
                interfaceId,
                includeInterfaceView: true,
                includeCreatedEventBlob: false,
              },
            },
          },
        },
      ],
    };
  }

  private static createEventFormat(key: ACSKey): EventFormat {
    const cumulativeFilter = key.templateId
      ? ACSContainer.createTemplateFilter(key.templateId)
      : ACSContainer.createInterfaceFilter(key.interfaceId ?? '');

    if (key.party) {
      return {
        filtersByParty: {
          [key.party]: cumulativeFilter,
        },
        verbose: false,
      };
    }
    return {
      filtersByParty: {},
      filtersForAnyParty: cumulativeFilter,
      verbose: false,
    };
  }
}

interface ACSSet {
  // offset at which initialAcs is valid
  acsOffset: number;
  // the initial ACS at acsOffset
  initialAcs: Array<JsGetActiveContractsResponse>;
  // last seen update offset
  lastUpdateOffset: number;
  // all updates since acsOffset - will be used to calculate ACS at any offset >= acsOffset
  // may be compacted (see ACSUpdateConfig.maxEventsBeforePrune and ACSUpdateConfig.safeOffsetDeltaForPrune)
  updates: Array<ACUpdateEvent>;
}

interface ACUpdateEvent {
  offset: number;
  created: CreatedEvent | null;
  archivedContractId: string | null;
  workflowId: string | null;
  synchronizerId: string | null;
}
